import { CalendarRange, Sparkles, Users, Vote, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import { Heading } from "@/components/ui/heading";

const STEP_COLORS = [
  { bg: "bg-brand-navy dark:ring-1 dark:ring-[hsl(40_26%_93%/0.2)]", text: "text-white" },
  { bg: "bg-brand-coral", text: "text-white" },
  { bg: "bg-brand-gold", text: "text-black" },
  { bg: "bg-brand-teal dark:ring-1 dark:ring-[hsl(40_26%_93%/0.2)]", text: "text-white" },
  { bg: "bg-brand-navy dark:ring-1 dark:ring-[hsl(40_26%_93%/0.2)]", text: "text-white" },
];

export function HowItWorks() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [ref, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.08 });

  const steps = [
    { icon: CalendarRange, title: t("howItWorks.step1Title"), description: t("howItWorks.step1Desc"), href: "/travel-calendar" },
    { icon: Users, title: t("howItWorks.step2Title"), description: t("howItWorks.step2Desc"), href: "/browse" },
    { icon: Sparkles, title: t("howItWorks.step3Title"), description: t("howItWorks.step3Desc"), href: "/create-trip" },
    { icon: Vote, title: t("howItWorks.step4Title"), description: t("howItWorks.step4Desc"), href: "/expenses" },
    { icon: Share2, title: t("howItWorks.step5Title"), description: t("howItWorks.step5Desc"), href: "/community" },
  ];

  const handleClick = (href: string) => (e: React.MouseEvent) => {
    if (!isAuthenticated) {
      e.preventDefault();
      window.location.href = "/sign-up";
    }
  };

  return (
    <section
      ref={ref}
      id="how-it-works"
      className="py-20 md:py-28 lg:py-36 scroll-mt-20 bg-muted/30 dark:bg-muted/10"
      data-testid="section-how-it-works"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16 lg:mb-20">
          <Heading
            level={1}
            as="h2"
            serif
            className="text-3xl md:text-4xl lg:text-5xl font-black mb-4"
          >
            {t("howItWorks.title")}
          </Heading>
          <p className="text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("howItWorks.subtitle")}
          </p>
        </div>

        {/* Desktop: Horizontal timeline */}
        <div className="hidden lg:grid lg:grid-cols-5 gap-6 relative">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const color = STEP_COLORS[index];
            return (
              <a
                key={index}
                href={step.href}
                onClick={handleClick(step.href)}
                className={`relative text-center group cursor-pointer transition-all duration-500 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: `${index * 120}ms` }}
                data-testid={`card-step-${index}`}
              >
                {/* Watermark step number */}
                <span
                  className="absolute -top-6 left-1/2 -translate-x-1/2 text-[7rem] font-black leading-none text-foreground/[0.04] dark:text-foreground/[0.06] select-none pointer-events-none"
                >
                  {index + 1}
                </span>

                {/* Icon circle */}
                <div className={`relative z-10 w-16 h-16 mx-auto mb-5 rounded-full ${color.bg} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <StepIcon className={`h-7 w-7 ${color.text}`} />
                </div>

                {/* Gold arrow connector (not on last) */}
                {index < steps.length - 1 && (
                  <div className="absolute top-8 left-[calc(50%+2.5rem)] w-[calc(100%-3rem)] flex items-center justify-center pointer-events-none">
                    <div className="w-full h-0.5 bg-brand-gold/40" />
                    <svg className="shrink-0 -ml-1 text-brand-gold/60" width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                      <path d="M2 1 L10 6 L2 11 Z" />
                    </svg>
                  </div>
                )}

                <div className="text-xs font-bold text-muted-foreground mb-1 uppercase tracking-wider">
                  {t("howItWorks.stepLabel", { number: index + 1 })}
                </div>
                <Heading level={4} as="h3" className="text-lg font-bold mb-2">
                  {step.title}
                </Heading>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </a>
            );
          })}
        </div>

        {/* Mobile: Vertical numbered list with dotted line */}
        <div className="lg:hidden space-y-0 relative">
          {/* Dotted connecting line */}
          <div className="absolute left-6 top-4 bottom-4 w-px border-l-2 border-dashed border-brand-gold/30" />

          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const color = STEP_COLORS[index];
            return (
              <a
                key={index}
                href={step.href}
                onClick={handleClick(step.href)}
                className="relative flex items-start gap-5 py-6 px-2 group"
                data-testid={`card-step-${index}`}
              >
                {/* Number circle */}
                <div className={`relative z-10 shrink-0 w-12 h-12 rounded-full ${color.bg} flex items-center justify-center shadow-md`}>
                  <StepIcon className={`h-5 w-5 ${color.text}`} />
                </div>

                <div className="pt-1">
                  <div className="text-xs font-bold text-muted-foreground mb-0.5 uppercase tracking-wider">
                    {t("howItWorks.stepLabel", { number: index + 1 })}
                  </div>
                  <Heading level={4} as="h3" className="text-base font-bold mb-1">
                    {step.title}
                  </Heading>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
